import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useSetRecorder — captures a MediaStream to a downloadable audio/webm blob.
 *
 *   const rec = useSetRecorder(() => engine.getBroadcastStream());
 *   <button onClick={rec.start}>REC</button>
 *   <button onClick={rec.stop}>STOP</button>
 *   {rec.blobUrl && <a href={rec.blobUrl} download={rec.suggestedFilename}>Download</a>}
 *
 * Keeps chunks in memory — for a 1hr set at 96kbps that's ~42MB which is
 * fine for browser RAM. Large sets should use IndexedDB, left as a future
 * pass.
 */
export default function useSetRecorder(getStream, { label = "tapin-radio-set" } = {}) {
  const [recording, setRecording] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [blobFilename, setBlobFilename] = useState(null);
  const [durationSec, setDurationSec] = useState(0);
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(null);
  const tickerRef = useRef(0);

  const cleanupBlob = () => {
    setBlobUrl((url) => { if (url) { try { URL.revokeObjectURL(url); } catch {} } return null; });
  };

  const start = useCallback(() => {
    setError(null);
    const stream = typeof getStream === "function" ? getStream() : getStream;
    if (!stream) { setError("No broadcast stream available — play some audio first."); return false; }
    try {
      cleanupBlob();
      chunksRef.current = [];
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 128000 }) : new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        const url = URL.createObjectURL(blob);
        // Capture timestamp NOW so the filename is stable across re-renders.
        const ts = new Date().toISOString().slice(0, 16).replace("T", "-").replace(":", "");
        setBlobFilename(`${label}-${ts}.webm`);
        setBlobUrl(url);
      };
      rec.start(1000);  // 1s timeslice for streaming chunks
      recRef.current = rec;
      startedAtRef.current = Date.now();
      setRecording(true);
      setDurationSec(0);
      tickerRef.current = setInterval(() => {
        setDurationSec(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 500);
      return true;
    } catch (e) {
      setError(String(e?.message || e));
      return false;
    }
  }, [getStream]);

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch {}
    recRef.current = null;
    clearInterval(tickerRef.current);
    setRecording(false);
  }, []);

  useEffect(() => () => {
    try { recRef.current?.stop(); } catch {}
    clearInterval(tickerRef.current);
    cleanupBlob();
  }, []);

  // ISO-ish timestamp filename is now captured on stop (stable across renders).
  return { recording, blobUrl, durationSec, error, start, stop, suggestedFilename: blobFilename };
}
